import { observable, action, makeObservable } from 'mobx';
import agent from '../agent';

class NotificationStore {
    constructor() {
        makeObservable(this, {
            masterNotifications: observable,
            templates: observable,
            templatesPages: observable,
            clientMessages: observable,
            loading: observable,
            loadMasterNotifications: action,
            createMasterNotification: action,
            updateMasterNotification: action,
            updateMasterMessage: action,
            createMasterMessage: action,
            loadTemplates: action,
            createTemplate: action,
            updateTemplate: action,
            deleteTemplate: action,
            loadClientMessages: action,
            upsertClientMessage: action,
            deleteClientMessage: action,
            bulkAssignNotificationTemplate: action,
            loadNotificationUsers: action,
            sendImmediateNotificationToUser: action,
            loadUserNotificationSettings: action,
            saveUserNotificationSettings: action,
            loadUserChannelPreferences: action,
            saveUserChannelPreferences: action,
            setLastInteractedId: action,
            clearLastInteractedId: action,
        });
    }

    masterNotifications = {};
    templates = [];
    templatesPages = 1;
    clientMessages = {};
    loading = false;
    lastInteractedId = null;

    setLastInteractedId(id) {
        this.lastInteractedId = id ?? null;
    }

    clearLastInteractedId() {
        this.lastInteractedId = null;
    }

    createMasterNotification(data) {
        return agent.Notifications.createMasterNotification(data)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    updateMasterNotification(id, data) {
        return agent.Notifications.updateMasterNotification(id, data)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    updateMasterMessage(id, data) {
        return agent.Notifications.updateMasterMessage(id, data)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    createMasterMessage(data) {
        return agent.Notifications.createMasterMessage(data)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    loadTemplates(params = {}) {
        this.loading = true;
        return agent.Notifications.getTemplates(params)
            .then(
                action(response => {
                    this.templates = response.data;
                    this.templatesPages = response.pages || 1;
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    createTemplate(data) {
        return agent.Notifications.createTemplate(data)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    updateTemplate(id, data) {
        return agent.Notifications.updateTemplate(id, data)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    deleteTemplate(id) {
        return agent.Notifications.deleteTemplate(id)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    loadMasterNotifications(force = false) {
        if (!force && Object.keys(this.masterNotifications).length > 0) {
            return Promise.resolve({ data: this.masterNotifications });
        }
        this.loading = true;
        return agent.Notifications.getMasterNotifications()
            .then(
                action(response => {
                    this.masterNotifications = response.data;
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    loadClientMessages() {
        return agent.Notifications.getClientMessages()
            .then(
                action(response => {
                    this.clientMessages = response.data;
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    upsertClientMessage(masterId, data) {
        return agent.Notifications.upsertClientMessage(masterId, data)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    deleteClientMessage(masterId) {
        return agent.Notifications.deleteClientMessage(masterId)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    bulkAssignNotificationTemplate(data) {
        return agent.Notifications.bulkAssignNotificationTemplate(data)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    loadNotificationUsers(params) {
        return agent.Notifications.getBulkAssignUsers(params)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    loadUserNotificationSettings(userId) {
        return agent.Notifications.getUserNotificationSettings(userId)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    saveUserNotificationSettings(userId, settings) {
        return agent.Notifications.saveUserNotificationSettings(userId, settings)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    loadUserChannelPreferences() {
        return agent.Notifications.getUserChannelPreferences()
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    saveUserChannelPreferences(preferences) {
        return agent.Notifications.saveUserChannelPreferences(preferences)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    sendImmediateNotificationToUser(data) {
        return agent.Notifications.sendImmediateNotificationToUser(data)
            .then(
                action(response => {
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }
}

export default new NotificationStore();
